import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { passkeySupported, getLoginOptions } from "@/lib/webauthn";
import { PasswordInput } from "@/components/PasswordInput";
import { Waves, Loader2, Fingerprint, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, verifyMfa, passkeyLogin, apiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null); // { mfa_token }
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [pkStage, setPkStage] = useState("idle"); // idle | loading | ready | authenticating
  const pkOptions = useRef(null);
  // Server-synced MFA lockout countdown (display only — NOT an independent security timer).
  const [lockUntil, setLockUntil] = useState(0);   // ms epoch target from server retry_after
  const [nowTick, setNowTick] = useState(Date.now());
  const lockRemaining = lockUntil ? Math.max(0, lockUntil - nowTick) : 0;
  const locked = lockRemaining > 0;
  const lockMMSS = `${Math.floor(lockRemaining / 60000)}:${String(Math.floor((lockRemaining % 60000) / 1000)).padStart(2, "0")}`;
  useEffect(() => {
    if (!lockUntil) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lockUntil]);
  useEffect(() => { if (lockUntil && lockRemaining <= 0) setLockUntil(0); }, [lockRemaining, lockUntil]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const data = await login(email, password);
      if (data.mfa_required) {
        setMfa(data);
        // If the 2nd factor is already locked, show the correct remaining time immediately.
        if (data.retry_after) setLockUntil(Date.now() + data.retry_after * 1000);
      } else navigate("/");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (locked) return; // temporarily locked — ignore useless submissions
    setErr(""); setBusy(true);
    try {
      await verifyMfa(mfa.mfa_token, code, useRecovery);
      navigate("/");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "mfa_locked") {
        setLockUntil(Date.now() + (d.retry_after || 0) * 1000);
        setErr("");
      } else {
        setErr(apiError(d) || e2.message);
      }
    } finally { setBusy(false); }
  };

  // Step 1 (tap): create ONE login challenge + options, then reveal the Face ID step.
  const startPasskey = async () => {
    setErr(""); setPkStage("loading");
    try {
      pkOptions.current = await getLoginOptions(null); // discoverable — no email needed
      setPkStage("ready");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message || "Couldn't start passkey sign-in");
      setPkStage("idle");
    }
  };

  // Step 2 (tap): get() runs synchronously inside this handler (iOS-safe).
  const finishPasskey = () => {
    setErr(""); setPkStage("authenticating");
    passkeyLogin(pkOptions.current)
      .then(() => navigate("/"))
      .catch((e2) => {
        if (e2.name === "NotAllowedError") setErr("Passkey sign-in was cancelled.");
        else setErr(apiError(e2.response?.data?.detail) || e2.message || "Passkey sign-in failed");
        pkOptions.current = null; // single-use challenge consumed/aborted — start fresh
        setPkStage("idle");
      });
  };

  const quick = (em) => { setEmail(em); setPassword(em.startsWith("employee") ? "Tech2026!" : em.startsWith("manager") ? "Manager2026!" : "ShadowOwner2026!"); };

  return (
    <div className="min-h-screen flex items-stretch" style={{ background: "var(--bg-app)" }}>
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 relative overflow-hidden"
        style={{ background: "var(--bg-surface)", borderRight: "1px solid var(--brd)" }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "var(--accent)" }}>
            <Waves size={26} color="#fff" />
          </div>
          <div>
            <div className="font-head text-2xl font-extrabold leading-none">SHADOW GUIDE</div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--txt2)" }}>FreshSurf Operating System</div>
          </div>
        </div>
        <div>
          <h1 className="font-head text-5xl font-extrabold leading-[0.95] mb-4">
            RUN THE FIELD.<br /><span style={{ color: "var(--accent)" }}>HIT THE STANDARD.</span>
          </h1>
          <p className="text-base max-w-md" style={{ color: "var(--txt2)" }}>
            The internal crew OS built for the tablet in your hand — jobs, estimates, payments, QC and performance.
          </p>
        </div>
        <div className="font-mono text-xs" style={{ color: "var(--muted)" }}>Six-Figure Standard · $1,960 / crew / day</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        {mfa ? (
          <form onSubmit={verify} className="w-full max-w-sm sg-card p-6 sm:p-8 sg-fade" data-testid="mfa-step">
            <h2 className="font-head text-3xl font-bold mb-1 flex items-center gap-2"><ShieldCheck size={26} style={{ color: "var(--accent)" }} /> Verify</h2>
            <p className="text-sm mb-6" style={{ color: "var(--txt2)" }}>
              {useRecovery ? "Enter one of your recovery codes." : "Enter the 6-digit code from your authenticator app."}
            </p>
            {locked && <div className="chip mb-4 w-full justify-center" data-testid="mfa-lock-countdown"
              style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", borderColor: "#EF4444" }}>
              Too many incorrect attempts. Try again in {lockMMSS}.
            </div>}
            {err && !locked && <div className="chip mb-4 w-full justify-center" data-testid="mfa-error"
              style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", borderColor: "#EF4444" }}>{err}</div>}
            <input className="sg-input mb-4" value={code} data-testid="mfa-code" autoFocus disabled={locked}
              inputMode={useRecovery ? "text" : "numeric"} placeholder={useRecovery ? "recovery code" : "123456"}
              onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ""))} />
            <button className="sg-btn sg-btn-accent w-full" disabled={busy || !code || locked} data-testid="mfa-verify-btn">
              {busy ? <Loader2 className="animate-spin" size={18} /> : locked ? `Locked — ${lockMMSS}` : "Verify & sign in"}
            </button>
            <button type="button" className="block text-center text-sm mt-4 w-full" style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}
              onClick={() => { setUseRecovery(!useRecovery); setCode(""); setErr(""); }} data-testid="mfa-recovery-toggle">
              {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm sg-card p-6 sm:p-8 sg-fade" data-testid="login-form">
            <h2 className="font-head text-3xl font-bold mb-1">Sign In</h2>
            <p className="text-sm mb-6" style={{ color: "var(--txt2)" }}>Authorized FreshSurf personnel only.</p>

            {err && <div className="chip mb-4 w-full justify-center" data-testid="login-error"
              style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", borderColor: "#EF4444" }}>{err}</div>}

            {passkeySupported() && (
              <>
                {pkStage === "ready" || pkStage === "authenticating" ? (
                  <button type="button" className="sg-btn sg-btn-accent w-full mb-4" onClick={finishPasskey} disabled={pkStage === "authenticating"} data-testid="passkey-continue-btn">
                    {pkStage === "authenticating" ? <Loader2 className="animate-spin" size={18} /> : <><Fingerprint size={18} /> Continue with passkey</>}
                  </button>
                ) : (
                  <button type="button" className="sg-btn w-full mb-4" onClick={startPasskey} disabled={busy || pkStage === "loading"} data-testid="passkey-signin-btn">
                    {pkStage === "loading" ? <Loader2 className="animate-spin" size={18} /> : <><Fingerprint size={18} /> Sign in with passkey</>}
                  </button>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px" style={{ background: "var(--brd)" }} />
                  <span className="text-xs" style={{ color: "var(--muted)" }}>or email &amp; password</span>
                  <div className="flex-1 h-px" style={{ background: "var(--brd)" }} />
                </div>
              </>
            )}

            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Email</label>
            <input className="sg-input mt-1 mb-4" type="email" value={email} data-testid="login-email"
              onChange={(e) => setEmail(e.target.value)} autoComplete="username webauthn" required />

            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Password</label>
            <PasswordInput className="mt-1 mb-6" value={password} data-testid="login-password"
              onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />

            <button className="sg-btn sg-btn-accent w-full" disabled={busy} data-testid="login-submit">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
            </button>

            <div className="text-center mt-4">
              <button type="button" onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link"
                className="text-sm" style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}>
                Forgot password?
              </button>
            </div>

            <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--brd)" }}>
              <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>Demo accounts</div>
              <div className="flex flex-wrap gap-2">
                {[["Owner", "Anthony.Fryer@freshsurf.co"], ["Manager", "manager@freshsurf.com"], ["Tech", "employee001@freshsurf.com"]].map(([l, em]) => (
                  <button type="button" key={em} onClick={() => quick(em)} data-testid={`quick-${l.toLowerCase()}`}
                    className="chip" style={{ background: "var(--bg-elev)", color: "var(--txt2)", borderColor: "var(--brd)", cursor: "pointer" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
